import { memo, useCallback, useMemo} from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { selectActiveLayout } from "../../selectors/home";
import { Box, List, ListItem, Typography } from "@mui/material";
import predefinedDashboardMenu from "../widgetDashboard/predefined/predefined.dashBoard.menu";
import { PredefinedDashboardMenuType } from "../widgetDashboard/widgetDashboard.interface";
import { ExtraSmallButton, NonInteractiveTooltip } from "../custom";
import { updateLayout } from "../../reducer/widgetDashboard.reducer";
import { selectActiveLayoutId } from "../../selectors/home";
import {ActionType, useUndoRedoContext} from "../UndoRedoFunctonalities/UndoRedo.context";
import { selectUserDashboards } from "../../selectors/layout";
import { useUser } from "../../context/UserContext";
import { FeatureCategory, FeatureName, FeatureSubCategory } from "../adminUserRestrictions/Restrictions.constants";
import { Group, setFeatureRestrictionDialog } from "../../reducer/user.reducer";

type ListProps = {
    dashboardList: PredefinedDashboardMenuType[],
    index: number
}

const PredefinedDashboardList = memo(({ dashboardList: dashboardList, index }: ListProps) => {
    const dispatch = useDispatch()
    const {push}=useUndoRedoContext()

    const activeLayout = useSelector(selectActiveLayout)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const dashboards = useSelector(selectUserDashboards);
    const { user: { hasAccess } } = useUser()

    const activeDashboard = useMemo(() => {
            if (!activeLayoutId || ! dashboards) return null;
            return dashboards.find(d => d.id === activeLayoutId);
        }, [activeLayoutId, dashboards])
        

    const handleDashboardClick = useCallback((dashboard: PredefinedDashboardMenuType, isSinglePane: boolean) => {
        if(!isSinglePane && !hasAccess(FeatureCategory.CHART, FeatureSubCategory.MULTIPANE, FeatureName.CHART_SPLIT)){
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Multi-chart layouts",
                group: Group.CHART
            }))
            return
        }
        if (!dashboard.wrapper.dashboardConfig) return
        const widgetKeys = Array.from(Object.keys(activeLayout.widgets))
        const prevSlots = widgetKeys.map((wk) => Number(wk))
        dashboard.wrapper.applyPreviousSlots(activeLayout.activeWidget ? [activeLayout.activeWidget, ...prevSlots.filter((ps) => ps !== activeLayout.activeWidget)] : prevSlots)
        const widgetCount = dashboard.wrapper.panesCount
        const newLayout = {
            ...dashboard.wrapper.dashboardConfig,
            syncSettings: { ...activeLayout.syncSettings },
            name: activeDashboard?.name,
            activeWidget: activeLayout.activeWidget
        }
        const slots = dashboard.wrapper.slots()
        widgetKeys.forEach((key, ind) => {
            if (ind >= widgetCount) return
            const slot = slots[ind]
            newLayout.widgets = {
                ...newLayout.widgets,
                [slot]: {
                    ...activeLayout.widgets[Number(key)],
                    slotId: slot,
                    settings: {
                        ...activeLayout.widgets[Number(key)].settings,
                        chartId: `${activeLayoutId}-${slot}`
                    }
                }
            }
        })
        if (widgetKeys.length < widgetCount) {
            for (let i = widgetKeys.length; i < widgetCount; i++) {
                const slot = slots[i]
                newLayout.widgets = {
                    ...newLayout.widgets,
                    [slot]: {
                        ...activeLayout.widgets[Number(widgetKeys[0])],
                        slotId: slot,
                        settings: {
                            ...activeLayout.widgets[Number(widgetKeys[0])].settings,
                            chartId: `${activeLayoutId}-${slot}`
                        }
                    }
                }
            }
        }
        dispatch(updateLayout({
            id: activeLayoutId,
            layout: newLayout
        }))
        const data=[{
            activeLayoutId: activeLayoutId,
            previousData: activeLayout,
            current: newLayout
        }
        ]

        push( data,'MULTI_PANE_SELECT', ActionType.MULTI_PANE_SELECT)

    }, [activeLayout, activeLayoutId])

    return <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.25, width: '100%', alignItems: 'center' }}>
        {dashboardList.map((dashboard, itemIndex) => {
            const singlePane = index==0 && itemIndex==0
            return (
                <ExtraSmallButton
                    key={dashboard.id}
                    color="inherit"
                    sx={{ position: 'relative', padding: 0.5 }}
                    onClick={()=>{handleDashboardClick(dashboard, singlePane)}}
                >
                    {dashboard.icon}
                </ExtraSmallButton>
                )}
            )
        }
    </Box>
})

const PredefinedDashboardMenu = memo(() => {

    return <List sx={{ p: 0 }}>
        {
            predefinedDashboardMenu.map((dashboardList, ind) => {
                return (
                    <ListItem key={ind} divider={ind !== predefinedDashboardMenu.length - 1}
                              sx={{ padding: 0.5, px: '12px', borderBottomColor: theme => theme.palette.divider }}>
                        <PredefinedDashboardList dashboardList={dashboardList} index={ind} />
                    </ListItem>
                )
            })
        }
    </List>
})

export default PredefinedDashboardMenu
